import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BarChart3, ExternalLink, Loader2, Pause, Play, RefreshCw, Rocket } from 'lucide-react';
import { SnapchatGhostIcon } from './SnapchatAccountSection';
import { snapchatAdsManagerUrl } from './snapchatConfig';

const statusStyle = (status) => (status === 'ACTIVE' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300');
const reviewStyle = (status) => (status === 'APPROVED' ? 'text-green-300' : status === 'REJECTED' ? 'text-red-300' : 'text-amber-300');
const fmt = (n) => (n || 0).toLocaleString();

const CampaignCard = ({ campaign, currency, onToggle, onActivate, busy }) => {
  const isActive = campaign.status === 'ACTIVE';
  const pendingAds = campaign.ads.filter((a) => a.review_status && a.review_status !== 'APPROVED');
  return (
    <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3" data-testid={`snapchat-campaign-${campaign.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white truncate">{campaign.name}</h3>
          <p className="text-xs text-white/50">{campaign.objective?.replace(/_/g, ' ')} • {campaign.ad_squads.length} ad squad{campaign.ad_squads.length === 1 ? '' : 's'} • {campaign.ads.length} ad{campaign.ads.length === 1 ? '' : 's'}</p>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusStyle(campaign.status)}`} data-testid="snapchat-campaign-status">{campaign.status}</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[
          ['Spend', `${currency || ''} ${fmt(campaign.spend)}`],
          ['Impressions', fmt(campaign.impressions)],
          ['Swipe Ups', fmt(campaign.swipes)],
          ['Budget', `${currency || ''} ${fmt(campaign.daily_budget || campaign.lifetime_budget)}${campaign.daily_budget ? '/day' : ''}`],
        ].map(([label, value]) => (
          <div key={label} className="p-2 rounded-lg bg-black/20">
            <p className="text-[10px] uppercase tracking-wide text-white/40">{label}</p>
            <p className="text-xs font-semibold text-white truncate">{value}</p>
          </div>
        ))}
      </div>
      {pendingAds.length > 0 && (
        <p className="text-xs text-white/60">
          Review: {pendingAds.map((a) => <span key={a.id} className={`ml-1 ${reviewStyle(a.review_status)}`}>{a.review_status.replace(/_/g, ' ')}</span>)}
        </p>
      )}
      <div className="flex gap-2">
        {!isActive && campaign.ads.length > 0 && (
          <Button size="sm" disabled={busy} onClick={() => onActivate(campaign)} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs" data-testid="snapchat-activate-btn">
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Rocket className="h-3 w-3 mr-1" />} Activate & Submit for Review
          </Button>
        )}
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onToggle(campaign)} className="border-white/20 text-white/80 hover:bg-white/10 text-xs" data-testid="snapchat-toggle-status-btn">
          {isActive ? <><Pause className="h-3 w-3 mr-1" /> Pause</> : <><Play className="h-3 w-3 mr-1" /> Resume Campaign</>}
        </Button>
      </div>
    </div>
  );
};

export const SnapchatCampaignsPanel = ({ snap, onNewCampaign }) => {
  const { connection, selectedAccount, campaigns, isLoadingCampaigns, fetchCampaigns, setEntityStatus, activateCampaign, connect } = snap;
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (connection?.connected && selectedAccount?.id) fetchCampaigns(selectedAccount.id);
  }, [connection?.connected, selectedAccount?.id, fetchCampaigns]);

  const run = async (campaign, action, successMsg) => {
    setBusyId(campaign.id);
    try {
      await action();
      toast.success(successMsg);
      await fetchCampaigns(selectedAccount.id);
    } catch (error) {
      toast.error('Snapchat update failed', { description: error.message });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="snapchat-campaigns-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#FFFC00] flex items-center justify-center text-black"><BarChart3 className="h-4 w-4" /></div>
          <div>
            <h2 className="font-semibold text-white">My Snapchat Campaigns</h2>
            <p className="text-xs text-white/50">{selectedAccount ? selectedAccount.name : 'Select an ad account'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={!selectedAccount || isLoadingCampaigns} onClick={() => fetchCampaigns(selectedAccount?.id)} className="border-white/20 text-white/70 hover:text-white hover:bg-white/10 text-xs" data-testid="snapchat-refresh-campaigns-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${isLoadingCampaigns ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={onNewCampaign} className="bg-[#FFFC00] hover:bg-[#f0ed00] text-black text-xs font-semibold" data-testid="snapchat-new-campaign-btn">+ New Campaign</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-400/10 border border-yellow-400/20">
        <SnapchatGhostIcon className="h-4 w-4 text-[#FFFC00]" />
        <span className="text-xs text-white/60">Source:</span>
        <span className="text-xs text-yellow-200 font-medium">Snapchat Marketing API (lifetime metrics)</span>
        {selectedAccount && (
          <a href={snapchatAdsManagerUrl(selectedAccount.id)} target="_blank" rel="noopener noreferrer" className="ml-auto flex items-center gap-1 text-xs text-yellow-200 hover:text-white" data-testid="snapchat-ads-manager-link">
            <ExternalLink className="h-3 w-3" /> Ads Manager
          </a>
        )}
      </div>

      {!connection?.connected ? (
        <div className="p-6 rounded-xl border border-dashed border-white/20 text-center space-y-3">
          <p className="text-sm text-white/60">Connect your Snapchat account to see campaigns.</p>
          <Button onClick={connect} className="bg-[#FFFC00] hover:bg-[#f0ed00] text-black font-semibold" data-testid="snapchat-campaigns-connect-btn">Connect Snapchat</Button>
        </div>
      ) : isLoadingCampaigns && campaigns.length === 0 ? (
        <div className="flex items-center justify-center py-10 text-white/60"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading campaigns...</div>
      ) : campaigns.length === 0 ? (
        <div className="p-6 rounded-xl border border-dashed border-white/20 text-center" data-testid="snapchat-no-campaigns">
          <p className="text-sm text-white/60">No campaigns yet in this ad account. Create your first Snapchat campaign.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              currency={selectedAccount?.currency}
              busy={busyId === campaign.id}
              onActivate={(c) => run(c, () => activateCampaign(c.id), 'Campaign activated — Snapchat is now reviewing your ad')}
              onToggle={(c) => run(c, () => setEntityStatus('campaigns', c.id, c.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE'), c.status === 'ACTIVE' ? 'Campaign paused' : 'Campaign resumed')}
            />
          ))}
        </div>
      )}
    </div>
  );
};
